/**
 * MediaMessage Component
 *
 * Renders media chat messages including images, videos, and stickers.
 * Supports gallery navigation, captions, and reply context.
 */

import React, { FC, useState, useEffect } from "react";
import clsx from "clsx";
import { convertTextStyle } from "src/util/TextFormatter";
import ChatMessageReplyView from "src/app/layout/chat-handle/components/chat-replymessage/ChatMessageReplyView";
import { MediaMessageProps } from "../../types/MessageTypes";
import { formatSize } from "src/app/modules/util/Utils";
import { getFilenameWithExtension } from "src/app/modules/chat/services";
import Message from "src/app/models/Message";
import { useMediaModal } from "../../context/MediaModalContext";
import { ThumbnailUtil } from "src/utils/thumbnailUtil";

// Helper to get media URL with fallback
const getMediaUrl = (message: Message): string | undefined => {
  return message.media?.originalUrl || message.media?.url || message.mediaUrl;
};

// Helper to get filename with fallback
const getFilename = (message: Message): string | undefined => {
  return message.media?.filename || message.filename;
};

// Helper to get formatted file size
const getFileSize = (message: Message): string | null => {
  if (message.media?.size) {
    return formatSize(message.media.size);
  }
  if (message.filesize) {
    return message.filesize;
  }
  return null;
};

// Helper to get duration for audio/video
const getDuration = (message: Message): string | null => {
  return message.media?.duration || message.duration || null;
};

const MediaMessage: FC<MediaMessageProps> = ({
  message,
  bgChat,
  txChat,
  isTabletOrMobile,
  replyMessageID,
  customer,
  phoneNumber,
  allMediaInRoom = [],
  previousMedia = "",
  nextMedia = "",
}) => {
  const { openModal } = useMediaModal();
  const messageType = message.messageType?.toLowerCase();
  const maxWidthClass = isTabletOrMobile ? "mw-200px" : "mw-300px";

  // Thumbnail state for localStorage
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);

  // Get media properties with fallbacks
  const mediaUrl = getMediaUrl(message);
  const filename = getFilename(message);
  const fileSize = getFileSize(message);
  const duration = getDuration(message);

  // Load thumbnail from localStorage on mount
  useEffect(() => {
    console.log("[MediaMessage] useEffect triggered:", {
      messageId: message.id,
      messageType: messageType,
      hasMessageId: !!message.id,
      isImageType: messageType === "image",
    });

    if (message.id && messageType === "image") {
      console.log("[MediaMessage] Loading thumbnail for message:", message.id);
      const cached = ThumbnailUtil.getThumbnail(message.id);
      if (cached) {
        console.log(
          "[MediaMessage] Thumbnail found in localStorage, size:",
          cached.length
        );
        setThumbnailUrl(cached);
      } else {
        console.log(
          "[MediaMessage] No thumbnail found in localStorage for:",
          message.id
        );
      }
    }
  }, [message.id, messageType]);

  /**
   * Handle media click to open modal
   */
  const handleMediaClick = () => {
    // Transform allMediaInRoom to MediaItem format
    const mediaItems = allMediaInRoom.map((item) => ({
      id: item.id,
      type: item.type,
      url: item.url,
      thumbnail: item.thumbnail,
      name: item.name,
    }));

    openModal(message.id, mediaItems);
  };

  /**
   * Renders image content
   */
  const renderImage = () => {
    // Use thumbnail if available, fallback to full image
    const displayUrl = thumbnailUrl || mediaUrl;

    console.log("[MediaMessage] renderImage called:", {
      messageId: message.id,
      hasThumbnail: !!thumbnailUrl,
      thumbnailLength: thumbnailUrl?.length,
      hasMediaUrl: !!mediaUrl,
      displayUrl: displayUrl?.substring(0, 50) + "...",
    });

    return (
      <div
        className={clsx("d-flex flex-column", maxWidthClass)}
        style={{ width: "auto", position: "relative" }}
      >
        <div
          className="btn d-flex flex-column rounded-1"
          onClick={handleMediaClick}
          style={{ cursor: "pointer" }}
        >
          <img
            src={displayUrl}
            data-full-url={mediaUrl}
            alt={filename || "image"}
            className={clsx(
              "img-fit-cover object-fit-cover rounded-1",
              maxWidthClass
            )}
            style={{
              maxHeight: "200px",
              objectFit: "cover",
            }}
            loading="lazy"
          />
        </div>
      </div>
    );
  };

  /**
   * Renders video content with play button overlay
   */
  const renderVideo = () => (
    <div
      className={clsx("d-flex flex-column", maxWidthClass)}
      style={{ width: "auto", position: "relative" }}
    >
      <div
        className="btn d-flex flex-column rounded-1"
        onClick={handleMediaClick}
        style={{ cursor: "pointer" }}
      >
        <video
          src={mediaUrl}
          className={clsx(
            "img-fit-cover object-fit-cover rounded-1",
            maxWidthClass
          )}
          style={{
            maxHeight: "200px",
            objectFit: "cover",
          }}
        />
        {/* Play button overlay */}
        <div
          className="position-absolute top-50 start-50 translate-middle"
          style={{
            backgroundColor: "rgba(0, 0, 0, 0.5)",
            borderRadius: "50%",
            padding: "10px 10px",
          }}
        >
          <i
            className="bi bi-play-fill text-white fs-2"
            style={{
              padding: "0",
            }}
          />
        </div>
        {/* Duration badge */}
        {duration && (
          <div
            className="position-absolute bottom-0 start-0 m-1 px-2 rounded"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              fontSize: "11px",
              color: "#fff",
            }}
          >
            {duration}
          </div>
        )}
        {/* Filesize badge */}
        {fileSize && (
          <div
            className="position-absolute bottom-0 end-0 m-1 px-2 rounded"
            style={{
              backgroundColor: "rgba(0, 0, 0, 0.6)",
              fontSize: "11px",
              color: "#fff",
            }}
          >
            {fileSize}
          </div>
        )}
      </div>
    </div>
  );

  /**
   * Renders sticker content
   */
  const renderSticker = () => (
    <div
      className="btn d-flex flex-column rounded-1"
      onClick={handleMediaClick}
      style={{ cursor: "pointer" }}
    >
      <img
        src={mediaUrl}
        alt={filename || "sticker"}
        className={clsx("img-fit-cover rounded-1")}
        style={{
          maxWidth: "150px",
          maxHeight: "150px",
          objectFit: "contain",
        }}
        loading="lazy"
      />
    </div>
  );

  /**
   * Renders the appropriate media based on type
   */
  const renderMedia = () => {
    switch (messageType) {
      case "video":
        return renderVideo();
      case "sticker":
        return renderSticker();
      case "image":
      case "image_with_text":
      default:
        return renderImage();
    }
  };

  /**
   * Renders caption if present
   */
  const renderCaption = () => {
    if (!message.textContent || message.textContent === "") {
      return null;
    }

    return (
      <div
        className={clsx(
          `p-3 rounded ${bgChat} fluid align-items-center`,
          maxWidthClass
        )}
        style={{ color: txChat }}
        dangerouslySetInnerHTML={{
          __html: convertTextStyle(message.textContent).replaceAll(
            "\n",
            "<br/>"
          ),
        }}
      />
    );
  };

  /**
   * Renders media with reply context
   */
  const renderWithReply = () => (
    <div className="d-flex flex-column">
      <ChatMessageReplyView
        bgChat={bgChat}
        replyMessageID={replyMessageID}
        customer={customer}
        phoneNumber={phoneNumber}
      >
        {renderMedia()}
      </ChatMessageReplyView>
      {renderCaption()}
    </div>
  );

  /**
   * Renders media without reply context
   */
  const renderStandard = () => (
    <div className="d-flex flex-column">
      {renderMedia()}
      {renderCaption()}
    </div>
  );

  // Render with or without reply context
  if (message.replyMessageID) {
    return renderWithReply();
  }

  return renderStandard();
};

export default MediaMessage;
